import { useContext, useEffect } from 'react';
import {
	Link,
	useLinkProps,
	useLocation,
	useParams,
} from '@tanstack/react-router';

import styles from './Preview.module.css';

import { Issue, Article, NavbarIconLogo } from '@/constants';
import config from '@/config';
import { useBackUrl } from '@/App.hooks';

import { Icon } from '@/components/icon/Icon';
import { useGetIssue } from '@/components/preview/Preview.hooks';
import { Loading } from '@/components/loading/Loading';

import { useTrackingPageView } from '@/tracking/Tracking.hooks';
import { AUTH_KEY } from '@/views/home/auth/Authentication.hooks';
import { logOut } from '@/templates/basic/auth/Auth.hooks';

import { PreviewContext } from './Preview.context';

const Preview = ({ onClickLogOut }: PreviewProps) => {
	const isAuthenticated = config.app.auth.enabled
		? !!localStorage.getItem(AUTH_KEY)
		: true;
	return (
		<PreviewContext.Provider
			value={{
				isAuthenticated,
				onClickLogOut,
			}}
		>
			<div className={styles['container']}>
				<View />
			</div>
		</PreviewContext.Provider>
	);
};

interface PreviewProps {
	onClickLogOut?: (url?: string) => void;
}

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
		</div>
	);
}

function Topbar() {
	return (
		<div className={styles['topbar']}>
			<div>
				<BackButton />
			</div>
			<div className={styles['title']}>
				<NavbarIconLogo />
			</div>
			<div>
				<div>
					<ButtonLogOut />
				</div>
			</div>
		</div>
	);
}

function BackButton() {
	const url = useBackUrl();
	return (
		<button id="back-button" type="button" title="Back">
			<Link to={url}>
				<Icon id={'back-btn'} name={'arrow-left'} color={'#333'} />
			</Link>
		</button>
	);
}

const ButtonLogOut = () => {
	const basename = useLinkProps({ to: '/logout' }).href || '/logout';
	const { isAuthenticated, onClickLogOut } = useContext(PreviewContext);
	if (!config.app.auth.enabled) {
		return null;
	}

	if (!onClickLogOut) {
		return null;
	}

	if (!isAuthenticated) {
		return null;
	}

	return (
		<button
			type={'button'}
			title="Log out"
			key={'log-out'}
			data-text="Log out"
			onClick={() => {
				onClickLogOut(basename);
				logOut(basename);
			}}
			className={'tooltip down'}
		>
			<Icon color="#000" name="logout" />
			Log Out
		</button>
	);
};

function Content() {
	const { id } = useParams({ from: '/preview/$id' });
	const location = useLocation();
	const { issue, isLoading } = useGetIssue(`${id}`);
	useTrackingPageView({
		title: issue?.name,
		location: location.pathname,
	});
	useEffect(() => {
		if (issue?.name) {
			document.title = issue.name;
		}
	}, [issue]);

	if (!id) {
		return null;
	}

	if (isLoading || issue === null || issue === undefined) {
		return <Loading />;
	}

	const { articles } = issue;

	return (
		<div className={styles['content']}>
			<div className={styles['cover']}>
				<Cover {...issue} />
				<Toc id={id} articles={articles} />
			</div>
		</div>
	);
}

function Cover(issue: Issue) {
	const { id, name, cover, slug, description } = issue;

	const from = window.location.pathname;
	const contentProtection = config?.app?.contentProtection;
	return (
		<section>
			<Link
				to={'/issues/$id'}
				params={{ id: slug ?? id }}
				from={from}
				className={styles['image']}
			>
				{contentProtection ? (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={500}
						height={667}
						className="content-protection"
						onContextMenu={e => e.preventDefault()}
					/>
				) : (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={500}
						height={667}
					/>
				)}
			</Link>
			<div className={styles['title']}>
				<h1>{name}</h1>
				{description ? <p>{description}</p> : null}
				<hr className="ml-auto mr-auto" />
			</div>
		</section>
	);
}

function Toc({ id, articles }: TocProps) {
	const filteredArticles = articles.filter(onlyIncludedToc).sort(sortByIndex);
	const from = window.location.pathname;
	return (
		<section>
			<ul className={styles['toc']}>
				{filteredArticles.map((article: Article) => (
					<li
						key={article.id}
						article-index={article.index}
						is-feature={article.isFeatured ? `` : undefined}
					>
						<Link
							to={`/issues/$id/$articleId`}
							params={{
								id,
								articleId: article.slug ?? article.id,
							}}
							from={from}
						>
							<h3>{article.name['en']}</h3>
						</Link>
					</li>
				))}
			</ul>
		</section>
	);
}

interface TocProps {
	id: string;
	articles: Array<Article>;
}

function sortByIndex(a: Article, b: Article): number {
	return a.index - b.index;
}

/*Exclude articles that are excluded from being display in Toc*/
function onlyIncludedToc(article: any) {
	if (article.role === 'advert') {
		return false;
	}
	return (
		article.channel === undefined ||
		article.channel === null ||
		article?.channel?.toc === 'included'
	);
}

export default Preview;
